/* eslint-disable no-nested-ternary */
import { Button, ButtonMode, ButtonSize, Filter, Icon, ICONS, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { isNumber, orderBy } from 'lodash';
import numeral from 'numeral';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import CountryFlag from '../../components/country-flag/CountryFlag';
import UpgradeSubscription from '../../components/no-access/UpgradeSubscription';
import { DEALS_PIPELINE_BONDS_ROUTE, DEALS_PIPELINE_COMBINED_ROUTE, DEALS_PIPELINE_LOANS_ROUTE, DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS, NO_DATA } from '../../Constants';
import useFetchSecurities from '../../hooks/securities/useFetchSecurities';
import useFetchSecurityFilters from '../../hooks/securities/useFetchSecurityFilters';
import useFetchUserPermissions from '../../hooks/user/useFetchUserPermissions';
import { DealMarketStatus } from '../../models/CompanyDeal';
import { Security } from '../../models/Securities';
import { formatDate } from '../../utils/date.utils';
import { filterMapper } from '../../utils/filter.utils';
import { formatResultsText, hasMoreData } from '../../utils/page.utils';
import { getDealsTabs, getDocumentUrl, getOfferMemorandumDocument } from './Securities.logic';
import SecurityDetails from './SecurityDetails';
// eslint-disable-next-line import/no-named-as-default
import '../../scss/filter-tsx.scss';
import './Securities.scss';
import NoPermissions from '../../components/NoPermissions';
// eslint-disable-next-line import/no-named-as-default

export interface SecuritiesProps {
  type: string;
  companyId?: string;
}

const EXCLUDED_COMPANY_FILTERS = ['company_name', 'sector'];

const Securities = ({ type, companyId }: SecuritiesProps): React.ReactElement => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const { isLoading: isPermissionsLoading, data: userPermissions } = useFetchUserPermissions();
  const isCompany = !!companyId;
  const isFilterKeyApplicable = (key) => !isCompany || !EXCLUDED_COMPANY_FILTERS.includes(key);
  const initialFilterConfig = isCompany ? { cs_companyid: [companyId] } : {};
  const PAGE_SIZE = isCompany ? 50 : 100;
  const [sort, setSort] = useState({ field: 'launch_date', asc: false });
  const [tabs, setTabs] = useState<DealMarketStatus[]>([]);
  const [marketStatus, setMarketStatus] = useState<DealMarketStatus>(isCompany ? DealMarketStatus.All : DealMarketStatus.InMarket);
  const [filterConfig, setFilterConfig] = useState<any>(initialFilterConfig);
  const { data: rawFilters, isLoading: filtersLoading } = useFetchSecurityFilters(type);
  const preparedFiltersConfig = useMemo(() => {
    if (!rawFilters) {
      return undefined;
    }

    const preparedFilters = { ...filterConfig };

    if (preparedFilters?.issuer_regions) {
      preparedFilters.issuer_regions = preparedFilters.issuer_regions.map((tagId) => rawFilters?.issuer_regions?.data.find((item) => item.key === tagId)?.title ?? tagId);
    }

    return preparedFilters;
  }, [filterConfig, rawFilters]);
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useFetchSecurities(type, sort, marketStatus, preparedFiltersConfig, PAGE_SIZE);
  const [expanded, setExpanded] = useState<any>({});

  const filters = useMemo(() => {
    if (rawFilters?.deal_status) {
      const dealsStatusFilter = { ...rawFilters.deal_status };
      const values = dealsStatusFilter.data.map((item) => item.key);
      const hasExpected = values.includes('Expected');

      if (hasExpected || values.includes('Rumoured')) {
        dealsStatusFilter.data = filterMapper([...values.filter((item) => item !== 'Rumoured'), ...(hasExpected ? [] : ['Expected'])]);
      }

      return {
        ...rawFilters,
        deal_status: dealsStatusFilter,
      };
    }

    return rawFilters;
  }, [rawFilters]);

  useEffect(() => {
    setFilterConfig((config) => (Object.keys(config).length > 0 ? initialFilterConfig : config));
  }, [type]);

  useEffect(() => {
    if (userPermissions) {
      const tabConfig = getDealsTabs(userPermissions);
      setTabs(tabConfig);
    }
  }, [userPermissions]);

  useEffect(() => {
    const encodedState = encodeURIComponent(JSON.stringify({ sort, marketStatus, filterConfig }));
    navigate(`${pathname}?state=${encodedState}`, { replace: true });
  }, [sort, marketStatus, filterConfig, navigate]);

  useEffect(() => {
    if (search) {
      try {
        const params = new URLSearchParams(search);
        const encodedState = params.get('state');
        if (encodedState) {
          const decodedState = JSON.parse(decodeURIComponent(encodedState));

          setSort(decodedState.sort);
          setMarketStatus(decodedState.marketStatus);
          setFilterConfig(decodedState.filterConfig);
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const toggleRow = (security) => {
    expanded[security.id] = !expanded[security.id];
    setExpanded({ ...expanded });
  };
  const getNavLinks = (companyId?) => {
    if (userPermissions?.permissions.includes('ui:deals_pipeline:combined_view')) {
      return [
        {
          title: 'Bonds',
          link: companyId ? `/companies/${companyId}/deals/bonds` : DEALS_PIPELINE_BONDS_ROUTE,
        },
        {
          title: 'Loans',
          link: companyId ? `/companies/${companyId}/deals/loans` : DEALS_PIPELINE_LOANS_ROUTE,
        },
        {
          title: 'Combined',
          link: companyId ? `/companies/${companyId}/deals/combined` : DEALS_PIPELINE_COMBINED_ROUTE,
        },
      ];
    }
    return [
      {
        title: 'Bonds',
        link: companyId ? `/companies/${companyId}/deals/bonds` : DEALS_PIPELINE_BONDS_ROUTE,
      },
      {
        title: 'Loans',
        link: companyId ? `/companies/${companyId}/deals/loans` : DEALS_PIPELINE_LOANS_ROUTE,
      },
    ];
  };

  const renderCompanyLink = (security) => {
    if (security.cs_companyid && !security.issuer_no_article_coverage) {
      return <NavLink to={`/companies/${security.cs_companyid}`}>{security.company_name}</NavLink>;
    }
    return <span>{security.company_name}</span>;
  };

  const renderCountryFlag = (security) => {
    if (!security.country_tag) {
      return NO_DATA;
    }

    return <CountryFlag countryName={security.country_text} iso3Code={security.country_tag} />;
  };

  const updateFilterConfig = (key, selected) => {
    setFilterConfig((config) => {
      if (key === 'deal_status' && selected.includes('Expected') && !selected.includes('Rumoured')) {
        selected.push('Rumoured');
      }

      // eslint-disable-next-line no-param-reassign
      config[key] = selected;

      if (selected.length === 0) {
        // eslint-disable-next-line no-param-reassign
        delete config[key];
      }

      return { ...config };
    });
  };

  const resetFilters = () => {
    setFilterConfig(initialFilterConfig);
  };

  const renderFilters = () => {
    return (
      <div className="filters-ctx">
        {Object.keys(filters)
          .filter(isFilterKeyApplicable)
          .map((key) => (
            <Filter key={key} data={filters[key].data} placeholder={filters[key].name} checkedKeys={filterConfig[key] || []} onChange={(checked) => updateFilterConfig(key, checked)} />
          ))}
        <div className="separator" />
        <button type="button" className="reset-button" onClick={resetFilters}>
          <Icon name={ICONS.Clear} width={12} height={12} /> Reset
        </button>
      </div>
    );
  };

  const renderMultiValueCell = (values: string[]) => {
    if (!values?.length) {
      return NO_DATA;
    }
    return orderBy(values).map((v) => <div key={v}>{v}</div>);
  };

  const updateSort = (field) => {
    setSort((v) => {
      if (v.field === field) {
        return {
          field,
          asc: !v.asc,
        };
      }
      return {
        field,
        asc: true,
      };
    });
  };

  const renderPercentage = (value) => {
    if (value || value === 0) {
      const p = value.replace('%', '');

      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(p * 1) && isNumber(p * 1)) {
        return `${numeral(p * 1).format('0.000')}%`;
      }
    }

    return value;
  };

  const renderOfferingMemorandumLink = (security: Security) => {
    const document = getOfferMemorandumDocument(security);
    return (
      document && (
        <a className="om-link" download href={getDocumentUrl(document)}>
          <Icon height={16} width={16} name={ICONS.Doc} />
        </a>
      )
    );
  };

  function renderOMCellContent(type, security) {
    if (type === 'bond') {
      return renderOfferingMemorandumLink(security) || NO_DATA;
    }

    if (type === 'combined' && security.security_type === 'bond') {
      return renderOfferingMemorandumLink(security) || NO_DATA;
    }

    return NO_DATA;
  }

  const renderNoAccessMessage = () => {
    return (
      <div className="no-access-ctx">
        <NoPermissions salesPersonEmail={DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS} />
      </div>
    );
  };

  if (isPermissionsLoading) {
    return <span />;
  }

  if (type === 'combined' && !userPermissions?.permissions.includes('ui:deals_pipeline:combined_view')) {
    return renderNoAccessMessage();
  }

  return (
    <div className={classNames('cs-securities-ctx', { 'is-company': isCompany })}>
      <div className="header-ctx">
        {!isCompany && (
          <div className="header">
            <div className="title">Deals Pipeline</div>
            <div className="separator" />
            <div className="nav-links">
              {getNavLinks(companyId).map((item) => (
                <NavLink key={`${item.title}-${item.link}`} to={item.link}>
                  {item.title}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="securities-ctx">
        {!filtersLoading && renderFilters()}
        <div className={classNames('tabs-ctx', { 'is-company': isCompany, 'to-translate': isCompany })}>
          {isCompany && (
            <div className="deal-types">
              {getNavLinks(companyId).map((item) => (
                <NavLink className="item" key={`${item.title}-${item.link}`} to={item.link}>
                  {item.title}
                </NavLink>
              ))}
            </div>
          )}
          {tabs.map((item) => (
            <div key={item} className={`tab ${marketStatus === item ? 'active' : 'inactive'}`} onClick={() => setMarketStatus(item)}>
              {item}
            </div>
          ))}
        </div>
        <div className="table-ctx">
          {!isCompany && <div className="section-title">{data?.pages[0]?.totalResults && formatResultsText(data)}</div>}
          {isLoading && (
            <div className="loading-ctx">
              <Loader className="cs-loader" position="center" />
            </div>
          )}
          {!isLoading && (
            <table className="ui table">
              <thead>
                <tr className="table-rows">
                  <th className="expander" />
                  {!isCompany && (
                    <th onClick={() => updateSort('company_name')}>
                      Company <i className="sort icon" />
                    </th>
                  )}
                  <th onClick={() => updateSort('issuers')}>
                    Issuer Name <i className="sort icon" />
                  </th>
                  {marketStatus === DealMarketStatus.All && (
                    <>
                      <th onClick={() => updateSort('deal_type')}>
                        Deal Type <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort(type === 'bond' ? 'pricing_date' : 'launch_date')}>
                        {type === 'bond' ? 'Pricing Date' : 'Launch Date'} <i className="sort icon" />
                      </th>
                      <th className="ui text right aligned" onClick={() => updateSort('size')}>
                        Size/CCY (mm)
                        <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort('uop')}>
                        UOP <i className="sort icon" />
                      </th>
                    </>
                  )}
                  {marketStatus === DealMarketStatus.InMarket && (
                    <>
                      <th onClick={() => updateSort('deal_type')}>
                        Deal Type <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort(type === 'bond' ? 'pricing_date' : 'launch_date')}>
                        {type === 'bond' ? 'Pricing Date' : 'Launch Date'} <i className="sort icon" />
                      </th>
                      {type === 'combined' ? (
                        <>
                          <th onClick={() => updateSort('commitment_date')}>
                            Commitment Date <i className="sort icon" />
                          </th>
                          <th onClick={() => updateSort('maturity_date')}>
                            Maturity Date <i className="sort icon" />
                          </th>
                        </>
                      ) : (
                        <th onClick={() => updateSort(type === 'bond' ? 'maturity_date' : 'commitment_date')}>
                          {type === 'bond' ? 'Maturity Date' : 'Commitment Date'} <i className="sort icon" />
                        </th>
                      )}
                      <th className="ui text right aligned" onClick={() => updateSort('size')}>
                        Size/CCY (mm)
                        <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort('uop')}>
                        UOP <i className="sort icon" />
                      </th>
                      {type === 'bond' && (
                        <th className="ui text right aligned" onClick={() => updateSort('last_px_talk')}>
                          Current PX Talk <i className="sort icon" />
                        </th>
                      )}
                    </>
                  )}

                  {marketStatus === DealMarketStatus.Expected && (
                    <>
                      <th onClick={() => updateSort('deal_type')}>
                        Deal Type <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort(type === 'bond' ? 'pricing_date' : 'launch_date')}>
                        {type === 'bond' ? 'Expected Pricing Date' : 'Expected Date'} <i className="sort icon" />
                      </th>
                      <th className="ui text right aligned" onClick={() => updateSort('size')}>
                        Expected Size/CCY (mm)
                        <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort('uop')}>
                        UOP <i className="sort icon" />
                      </th>
                    </>
                  )}

                  {marketStatus === DealMarketStatus.Completed && (
                    <>
                      <th onClick={() => updateSort('deal_type')}>
                        Deal Type <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort(type === 'bond' ? 'pricing_date' : 'closing_date')}>
                        {type === 'combined' ? 'Pricing Date' : type === 'bond' ? 'Pricing Date' : 'Closing Date'}
                        <i className="sort icon" />
                      </th>
                      {type === 'combined' && (
                        <th onClick={() => updateSort('maturity_date')}>
                          Maturity Date <i className="sort icon" />
                        </th>
                      )}
                      <th className="ui text right aligned" onClick={() => updateSort('size')}>
                        Size/CCY (mm)
                        <i className="sort icon" />
                      </th>
                      {type === 'bond' && (
                        <>
                          <th className="ui text right aligned" onClick={() => updateSort('coupon')}>
                            Coupon <i className="sort icon" />
                          </th>
                          <th className="ui text right aligned" onClick={() => updateSort('ytm')}>
                            YTM <i className="sort icon" />
                          </th>
                          <th onClick={() => updateSort('maturity_date')}>
                            Maturity Date <i className="sort icon" />
                          </th>
                        </>
                      )}
                      <th onClick={() => updateSort('uop')}>
                        UOP <i className="sort icon" />
                      </th>
                    </>
                  )}

                  {marketStatus === DealMarketStatus.Hung && (
                    <>
                      <th onClick={() => updateSort('deal_type')}>
                        Deal Type <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort(type === 'bond' ? 'pricing_date' : 'launch_date')}>
                        Postponed Date <i className="sort icon" />
                      </th>
                      <th className="ui text right aligned" onClick={() => updateSort(type === 'bond' ? 'size' : 'loan_size')}>
                        Size/CCY (mm)
                        <i className="sort icon" />
                      </th>
                      <th onClick={() => updateSort('uop')}>
                        UOP <i className="sort icon" />
                      </th>
                      {type === 'bond' && (
                        <th className="ui text right aligned" onClick={() => updateSort('last_px_talk')}>
                          Last PX Talk <i className="sort icon" />
                        </th>
                      )}
                    </>
                  )}

                  {!isCompany && (
                    <th onClick={() => updateSort('issuer_sectors')}>
                      Sector/Subsector <i className="sort icon" />
                    </th>
                  )}
                  {!isCompany && (
                    <th onClick={() => updateSort('issuer_regions')}>
                      Region <i className="sort icon" />
                    </th>
                  )}
                  <th onClick={() => updateSort('country_text')}>
                    Country or Territory <i className="sort icon" />
                  </th>
                  {(marketStatus === DealMarketStatus.All || marketStatus === DealMarketStatus.Completed) && (
                    <th onClick={() => updateSort('deal_status')}>
                      Deal status <i className="sort icon" />
                    </th>
                  )}
                  {(type === 'bond' || type === 'combined') && <th>OM</th>}
                </tr>
              </thead>
              {data?.pages[0]?.totalResults === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={16}>
                      <div className="no-results">
                        <Icon name={ICONS.NotFound} height={150} width={150} />
                        <div className="text">{formatResultsText(data)}</div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
              {data &&
                data.pages.map((page) =>
                  page.results.map((security) => (
                    <tbody key={`${security.security_type}-${security.id}`}>
                      <tr className={`table-rows ${expanded[security.id] ? 'expanded' : 'collapsed'}`}>
                        <td className="expander">
                          <Button mode={ButtonMode.TERTIARY} size={ButtonSize.MEDIUM} hasEqualSidesPadding onClick={() => toggleRow(security)}>
                            <Icon name={ICONS.CaretRight} />
                          </Button>
                        </td>
                        {!isCompany && <td>{renderCompanyLink(security)}</td>}
                        <td>{renderMultiValueCell(security.issuers)}</td>

                        {marketStatus === DealMarketStatus.All && (
                          <>
                            <td>{security.deal_type || NO_DATA}</td>
                            <td>{formatDate(type === 'bond' ? security.pricing_date : security.launch_date) || NO_DATA}</td>
                            <td className="ui text right aligned">{security.size ? `${security.size} ${security.currency}` : NO_DATA}</td>
                            <td>{security.uop || NO_DATA}</td>
                          </>
                        )}

                        {marketStatus === DealMarketStatus.InMarket && (
                          <>
                            <td>{security.deal_type || NO_DATA}</td>
                            <td>{formatDate(type === 'bond' ? security.pricing_date : security.launch_date) || NO_DATA}</td>
                            {type === 'combined' ? (
                              <>
                                <td>{formatDate(security.security_type === 'bond' ? security.pricing_date : security.commitment_date) || NO_DATA}</td>
                                <td>{formatDate(security.maturity_date) || NO_DATA}</td>
                              </>
                            ) : (
                              <td>{formatDate(type === 'bond' ? security.maturity_date : security.commitment_date) || NO_DATA}</td>
                            )}
                            <td className="ui text right aligned">{security.size ? `${security.size} ${security.currency}` : NO_DATA}</td>
                            <td>{security.uop || NO_DATA}</td>
                            {type === 'bond' && <td className="ui text right aligned">{security.last_px_talk || NO_DATA}</td>}
                          </>
                        )}

                        {marketStatus === DealMarketStatus.Expected && (
                          <>
                            <td>{security.deal_type || NO_DATA}</td>
                            <td>{formatDate(security.security_type === 'bond' ? security.pricing_date : security.launch_date) || NO_DATA}</td>
                            <td className="ui text right aligned">{security.size ? `${security.size} ${security.currency}` : NO_DATA}</td>
                            <td>{security.uop || NO_DATA}</td>
                          </>
                        )}

                        {marketStatus === DealMarketStatus.Completed && (
                          <>
                            <td>{security.deal_type || NO_DATA}</td>
                            <td>{formatDate(security.security_type === 'bond' ? security.pricing_date : security.closing_date) || NO_DATA}</td>
                            {type === 'combined' && <td>{security.security_type === 'bond' ? formatDate(security.maturity_date) || NO_DATA : NO_DATA}</td>}
                            <td className="ui text right aligned">{security.size ? `${security.size} ${security.currency}` : NO_DATA}</td>
                            {type === 'bond' && (
                              <>
                                <td className="ui text right aligned">{renderPercentage(security.coupon) || NO_DATA}</td>
                                <td className="ui text right aligned">{renderPercentage(security.ytm) || NO_DATA}</td>
                                <td>{formatDate(security.maturity_date) || NO_DATA}</td>
                              </>
                            )}
                            <td>{security.uop || NO_DATA}</td>
                          </>
                        )}

                        {marketStatus === DealMarketStatus.Hung && (
                          <>
                            <td>{security.deal_type || NO_DATA}</td>
                            <td>{formatDate(security.security_type === 'bond' ? security.pricing_date : security.launch_date) || NO_DATA}</td>
                            <td className="ui text right aligned">{security.size ? `${security.size} ${security.currency}` : NO_DATA}</td>
                            <td>{security.uop || NO_DATA}</td>
                            {type === 'bond' && <td className="ui text right aligned">{security.last_px_talk || NO_DATA}</td>}
                          </>
                        )}

                        {!isCompany && <td>{renderMultiValueCell(security.issuer_sectors)}</td>}
                        {!isCompany && <td>{renderMultiValueCell(security.issuer_regions)}</td>}
                        <td>{renderCountryFlag(security)}</td>
                        {(marketStatus === DealMarketStatus.All || marketStatus === DealMarketStatus.Completed) && <td>{(security.deal_status === 'Rumoured' ? 'Expected' : security.deal_status) || NO_DATA}</td>}
                        {type !== 'loan' && <td>{renderOMCellContent(type, security)}</td>}
                      </tr>
                      {expanded[security.id] && (
                        <tr className="bond-expander expanded">
                          <td colSpan={16}>
                            {security.can_access && <SecurityDetails security={security} />}
                            {!security.can_access && <UpgradeSubscription showHeader />}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ))
                )}
            </table>
          )}
          {hasMoreData(data, PAGE_SIZE, data?.pages[0]?.totalResults) && (
            <div className="next-page">
              <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isFetchingNextPage} disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Securities;
